import * as T from 'three';
import { atmosphere } from './stylize';

/**
 * Painted cumulus band (Bible §3: bright late-morning sky, soft clouds). Canvas rows map to
 * elevation: the bottom edge sits on the horizon, where clouds are flatter and smaller.
 */
function cloudTexture(seed: number) {
    let s = seed >>> 0 || 1;
    const random = () => ((s = Math.imul(s ^ s >>> 15, 2246822519) + 0x6D2B79F5 >>> 0) % 100000) / 100000;
    const W = 2048, H = 512, c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d')!;
    const puff = (x: number, y: number, r: number, base: number, lit: string, shade: string) => {
        for (const dx of [-W, 0, W]) {
            const g = ctx.createRadialGradient(x + dx - r * .25, y - r * .35, r * .1, x + dx, y, r);
            g.addColorStop(0, lit);
            g.addColorStop(.55, lit);
            g.addColorStop(.85, shade);
            g.addColorStop(1, 'rgba(214,230,240,0)');
            ctx.save();
            ctx.beginPath();
            ctx.rect(x + dx - r, y - r, r * 2, base - (y - r));
            ctx.clip();
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(x + dx, y, r, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }
    };
    for (let i = 0; i < 34; i++) {
        const elevation = random() ** 1.6;
        const base = H - 10 - elevation * (H * .8);
        const size = 14 + elevation * 46 + random() * 18;
        const x = random() * W, width = size * (3 + random() * 4);
        const blobs = 5 + Math.floor(random() * 7);
        for (let k = 0; k < blobs; k++) {
            const t = blobs === 1 ? .5 : k / (blobs - 1);
            const hump = Math.sin(Math.PI * t);
            const r = size * (.55 + hump * .65) * (.8 + random() * .4);
            puff(x + (t - .5) * width, base - r * .55 - hump * size * .5, r, base, 'rgba(255,255,253,0.97)', 'rgba(222,234,244,0.85)');
        }
    }
    const texture = new T.CanvasTexture(c);
    texture.wrapS = T.RepeatWrapping;
    texture.wrapT = T.ClampToEdgeWrapping;
    texture.minFilter = T.LinearFilter;
    texture.generateMipmaps = false;
    return texture;
}

/**
 * Gradient sky dome with soft painted clouds and a sun glow. Drawn last among opaque objects at
 * the far plane so early depth testing skips every pixel the jungle already covers.
 */
export class Sky {
    mesh: T.Mesh;
    private zenith = new T.Vector3();
    private sun = new T.Vector3(.4, .7, .4);
    constructor(seed: number, zenith = '#5ea8df') {
        const z = new T.Color(zenith).convertLinearToSRGB();
        this.zenith.set(z.r, z.g, z.b);
        const material = new T.ShaderMaterial({
            uniforms: {
                horizon: atmosphere.hazeColor,
                zenith: { value: this.zenith },
                sunDir: { value: this.sun },
                clouds: { value: cloudTexture(seed) },
                time: atmosphere.time,
            },
            vertexShader: `varying vec3 vDir;
                void main() {
                    vDir = position;
                    vec4 p = projectionMatrix * vec4(mat3(viewMatrix) * position, 1.0);
                    gl_Position = vec4(p.xy, p.w * .99999, p.w);
                }`,
            fragmentShader: `uniform vec3 horizon, zenith, sunDir; uniform sampler2D clouds; uniform float time; varying vec3 vDir;
                void main() {
                    vec3 d = normalize(vDir);
                    float h = d.y;
                    vec3 col = mix(horizon, zenith, pow(smoothstep(-.02, .62, h), .8));
                    float s = max(dot(d, sunDir), 0.0);
                    col += vec3(1.0, .9, .7) * (pow(s, 6.0) * .16 + pow(s, 400.0) * .9);
                    if (h > 0.0) {
                        vec2 uv = vec2(atan(d.x, d.z) / 6.2831853 * 2.0 + time * .0012, clamp(h / .5, 0.0, 1.0));
                        vec4 cloud = texture2D(clouds, uv);
                        float a = cloud.a * smoothstep(0.0, .07, h);
                        vec3 tint = mix(horizon, vec3(1.0), .55 + .45 * smoothstep(0.0, .3, h));
                        col = mix(col, cloud.rgb * tint + vec3(1.0, .93, .78) * pow(s, 3.0) * .12, a);
                    }
                    gl_FragColor = vec4(col, 1.0);
                }`,
            depthWrite: false,
            fog: false,
            side: T.BackSide,
        });
        this.mesh = new T.Mesh(new T.SphereGeometry(10, 32, 16), material);
        this.mesh.frustumCulled = false;
        this.mesh.renderOrder = 1e6;
        this.mesh.name = 'sky';
    }
    /** World-space direction toward the sun. */
    setSun(direction: T.Vector3) { this.sun.copy(direction).normalize(); }
}
